import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  telecomApi,
  type AbonneListParams, type CreateAbonnePayload, type UpdateAbonnePayload,
  type CreateHotspotPayload, type HotspotListParams, type UpdateHotspotPayload,
  type CreateTransactionPayload, type TransactionListParams, type UpdateTransactionPayload,
} from '@/api/telecom';

const HOTSPOTS_KEY     = ['operations', 'telecom', 'hotspots']     as const;
const ABONNES_KEY      = ['operations', 'telecom', 'abonnes']      as const;
const TRANSACTIONS_KEY = ['operations', 'telecom', 'transactions'] as const;

// ── Hotspots ────────────────────────────────────────────────────────────

export const useHotspots = (params: HotspotListParams = {}) =>
  useQuery({ queryKey: [...HOTSPOTS_KEY, params], queryFn: () => telecomApi.listHotspots(params), staleTime: 30_000 });

export const useHotspot = (id: string | undefined) =>
  useQuery({ queryKey: [...HOTSPOTS_KEY, id], queryFn: () => telecomApi.getHotspot(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateHotspot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateHotspotPayload) => telecomApi.createHotspot(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: HOTSPOTS_KEY }),
  });
};
export const useUpdateHotspot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateHotspotPayload }) => telecomApi.updateHotspot(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: HOTSPOTS_KEY }),
  });
};
export const useDeleteHotspot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => telecomApi.deleteHotspot(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: HOTSPOTS_KEY }),
  });
};

// ── Abonnés ─────────────────────────────────────────────────────────────

export const useAbonnes = (params: AbonneListParams = {}) =>
  useQuery({ queryKey: [...ABONNES_KEY, params], queryFn: () => telecomApi.listAbonnes(params), staleTime: 30_000 });

export const useAbonne = (id: string | undefined) =>
  useQuery({ queryKey: [...ABONNES_KEY, id], queryFn: () => telecomApi.getAbonne(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateAbonne = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateAbonnePayload) => telecomApi.createAbonne(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ABONNES_KEY }),
  });
};
export const useUpdateAbonne = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAbonnePayload }) => telecomApi.updateAbonne(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ABONNES_KEY }),
  });
};
export const useDeleteAbonne = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => telecomApi.deleteAbonne(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ABONNES_KEY }),
  });
};

// ── Transactions Mobile Money ──────────────────────────────────────────

export const useTransactions = (params: TransactionListParams = {}) =>
  useQuery({ queryKey: [...TRANSACTIONS_KEY, params], queryFn: () => telecomApi.listTransactions(params), staleTime: 30_000 });

export const useTransaction = (id: string | undefined) =>
  useQuery({ queryKey: [...TRANSACTIONS_KEY, id], queryFn: () => telecomApi.getTransaction(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateTransactionPayload) => telecomApi.createTransaction(p),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY });
      qc.invalidateQueries({ queryKey: ABONNES_KEY }); // refetch solde data
    },
  });
};
export const useUpdateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTransactionPayload }) => telecomApi.updateTransaction(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY });
      qc.invalidateQueries({ queryKey: ABONNES_KEY });
    },
  });
};
export const useDeleteTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => telecomApi.deleteTransaction(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
};
